import React, { useState, useContext, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Autocomplete,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
} from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import { isAuthExpiredDialogVisible, subscribeAuthExpired } from '../../../utils/auth-error-utils';

export default function PatientDetailsDialog({ open, onClose, onSubmit }) {
    const { authStore } = useContext(StoreContext);

    const [inputMethod, setInputMethod] = useState('manual'); // 'manual' or 'select'
    const [manualPatientName, setManualPatientName] = useState('');
    const [manualClientSurname, setManualClientSurname] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);

    useEffect(() => {
        // Fetch patients from API when dialog opens
        let isSubscribed = true;

        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            try {
                const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                    filterType: 'patientStatus',
                    filterValue: 'active',
                });

                if (!isSubscribed) {
                    return;
                }

                if (response?.patients) {
                    setPatients(response.patients);
                    if (response.patients.length === 0) {
                        setInputMethod('manual');
                    }
                } else {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } catch (error) {
                console.error('Failed to fetch patients:', error);
                if (isSubscribed) {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } finally {
                if (isSubscribed) {
                    setIsLoadingPatients(false);
                }
            }
        };

        if (open) {
            fetchPatients();
        }

        return () => {
            isSubscribed = false;
        };
    }, [open, authStore.idToken]);

    const resetForm = () => {
        setInputMethod('manual');
        setManualPatientName('');
        setManualClientSurname('');
        setSelectedPatient(null);
    };

    const handleSubmit = () => {
        let patientDetails = null;

        if (inputMethod === 'manual') {
            if (manualPatientName || manualClientSurname) {
                patientDetails = {
                    patientName: manualPatientName || null,
                    clientSurname: manualClientSurname || null,
                };
            }
        } else if (inputMethod === 'select' && selectedPatient) {
            patientDetails = {
                patientName: selectedPatient.patientName || null,
                clientSurname: selectedPatient.clientSurname || null,
                patientId: selectedPatient.patientId || null,
                microchipNumber: selectedPatient.microchipNumber || null,
                clinicPatientId: selectedPatient.clinicPatientId || null,
            };
        }

        onSubmit(patientDetails);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            handleClose();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            handleClose();
        });
    }, [open, handleClose]);

    const isSubmitDisabled = () => {
        if (inputMethod === 'manual') {
            return !manualPatientName && !manualClientSurname;
        }
        if (inputMethod === 'select') {
            return !selectedPatient;
        }
        return true;
    };

    const isBlocked = isAuthExpiredDialogVisible();

    return (
        <>
            <Dialog open={open && !isBlocked} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Patient Details</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Add patient details to personalize the shared handout.
                        </Typography>

                        <FormControl component="fieldset" sx={{ mb: 3 }}>
                            <RadioGroup value={inputMethod} onChange={(e) => setInputMethod(e.target.value)}>
                                <FormControlLabel value="manual" control={<Radio />} label="Enter patient details" />
                                <FormControlLabel
                                    value="select"
                                    control={<Radio />}
                                    label="Select existing patient"
                                    disabled={isLoadingPatients || patients.length === 0}
                                />
                            </RadioGroup>
                        </FormControl>

                        {inputMethod === 'manual' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Patient Name"
                                    value={manualPatientName}
                                    onChange={(e) => setManualPatientName(e.target.value)}
                                    fullWidth
                                    placeholder="e.g., Bella"
                                />
                                <TextField
                                    label="Keeper Surname"
                                    value={manualClientSurname}
                                    onChange={(e) => setManualClientSurname(e.target.value)}
                                    fullWidth
                                    placeholder="e.g., Smith"
                                />
                            </Box>
                        )}

                        {inputMethod === 'select' && (
                            <Autocomplete
                                options={patients}
                                getOptionLabel={(option) =>
                                    `${option.patientName || ''} - ${option.clientSurname || ''} ${
                                        option.clinicPatientId ? `(${option.clinicPatientId})` : ''
                                    }`.trim()
                                }
                                value={selectedPatient}
                                onChange={(event, newValue) => setSelectedPatient(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Patient" placeholder="Search patients..." />
                                )}
                                fullWidth
                                disabled={isLoadingPatients || patients.length === 0}
                            />
                        )}

                        {!isLoadingPatients && patients.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                No patients found. Use manual entry to add patient details.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled()}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <BlockingSpinner open={open && !isBlocked && isLoadingPatients} />
        </>
    );
}
